import React, { useState, useRef } from 'react';
import { ArrowDownTrayIcon, ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { LoaderSpinner } from '../LoaderSpinner';
import { useClickOutsideBlock } from '../../helpers/useClickOutside';

const CSVExportDropdown = ({ onExport, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const dropdownRef = useRef(null);

  useClickOutsideBlock(dropdownRef, () => setOpen(false));

  const handleExport = async (type) => {
    if (!onExport || disabled || isExporting) return;
    setIsExporting(true);
    setExportError(null);
    try {
      await onExport(type);
    } catch (err) {
      console.error('Export failed:', err);
      setExportError(err.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const getButtonStyles = () => {
    const base = 'inline-flex items-center px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200';
    return `${base} text-gray-900 bg-white border border-gray-400 hover:bg-gray-50 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed`;
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled || isExporting}
        className={getButtonStyles()}
      >
        {isExporting ? (
          <>
            <LoaderSpinner width="16" height="16" border="2" parentHeight="h-4" />
            <span className="ml-2">Exporting...</span>
          </>
        ) : (
          <>
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            <span>Download</span>
            <ChevronDownIcon className="h-4 w-4 ml-1" />
          </>
        )}
      </button>

      {open && !isExporting && (
        <div className="absolute right-0 z-10 mt-2 w-36 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
          <div className="py-1">
            <button
              onClick={() => { setOpen(false); handleExport('orders'); }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Orders
            </button>
            <button
              onClick={() => { setOpen(false); handleExport('items'); }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Line Items
            </button>
          </div>
        </div>
      )}

      {exportError && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-red-50 border border-red-200 rounded-md p-4 shadow-lg z-20">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Export Failed</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{exportError}</p>
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setExportError(null)}
                  className="text-sm font-medium text-red-800 hover:text-red-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSVExportDropdown;
